
import { computed, defineComponent, nextTick, reactive, ref, toRefs } from 'vue'
import { useI18n } from 'vue-i18n'
import { Team, TeamInfo } from '@/services/team'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { CentralControlService } from '@/views/dashboard/_components/central-control/central-control.service'
import { ElMessage, ElMessageBox } from 'element-plus'
import Copy from '@/views/_components/copy/copy.vue'

import {
  TeamInfoEditService
} from '@/views/dashboard/_components/central-control/_components/team-info-edit/team-info-edit.service'
import { router } from '@/router'
import { User } from '@/services/user'
import SmsVerify from '@/views/_components/sms-verify/sms-verify.vue'
import Cookies from 'js-cookie'
import { IS_PRIVATE } from '@/common/const/env'
const PASS_MIN = 6
const LATIN_MONOGRAM_LENGTH = 2
export default defineComponent({
  components: { SmsVerify, Copy },
  setup (props, context) {
    const { t } = useI18n()
    const injector = useReflectiveInjector([CentralControlService, TeamInfoEditService])
    const centralControlService = injector.get(CentralControlService)
    const service = injector.get(TeamInfoEditService)
    const userService = injector.get(User)
    const team = injector.get(Team)
    const mobile = ref('')
    const smsVerifyVisible = ref(false)
    const nameNode = ref<HTMLElement>()
    const exitCodeNode = ref<HTMLElement>()
    const uninstallCodeNode = ref<HTMLElement>()
    const teamInfo: TeamInfo = JSON.parse(Cookies.get('currentTeam'))
    const data = reactive({
      nameEdit: false,
      exitCodeEdit: false,
      unInstallCodeEdit: false,
      pin_code: teamInfo.pin_code || '',
      name: teamInfo.name || '',
      exitCode: teamInfo.exit_code || '',
      uninstallCode: teamInfo.uninstall_code || '',
      nameErrorFlag: false,
      uninstallCodeErrorFlag: false,
      exitCodeErrorFlag: false
    })

    userService.userInfo.subscribe(res => {
      mobile.value = res.mobile
    })

    const editDisabled = computed(() => {
      return data.nameErrorFlag || data.uninstallCodeErrorFlag || data.exitCodeErrorFlag ||
        data.nameEdit || data.unInstallCodeEdit || data.exitCodeEdit
    })

    /** 印章底款：中文/日文取首字，拉丁文取前两个字符 */
    const monogram = computed(() => {
      const trimName = data.name.trim()
      if (!trimName) return ''
      return /[぀-ヿ㐀-鿿]/.test(trimName.charAt(0)) ? trimName.charAt(0) : trimName.slice(0, LATIN_MONOGRAM_LENGTH)
    })

    function deleteTeam () {
      const strongHtml = `<strong>${t('teamInfoEdit.confirmDeleteTeamStrong', { name: data.name })}</strong>`
      const hintHtml = `<div style="font-size: 14px; color: var(--cm-color-text-secondary)">${t('teamInfoEdit.confirmDeleteTeamHint')}</div>`
      ElMessageBox.confirm(`${strongHtml}${hintHtml}`, t('centralControl.logoutConfirmTitle'), {
        confirmButtonText: t('common.confirm'),
        cancelButtonText: t('common.cancel'),
        confirmButtonClass:'cm-btn-del',
        dangerouslyUseHTMLString: true,
        customClass: 'team-info-edit-delete-team-message-box',
      }).then(() => {
        if (mobile.value) {
          smsVerifyVisible.value = true
        } else {
          confirmVerifyDeleteTeam()
        }
      })
    }

    function confirmVerifyDeleteTeam (data = {} as any) {
      service.deleteTeam({ code: data.code }).then(() => {
        ElMessage.success(t('teamInfoEdit.deregisterSuccess'))
        team.updateCurrentTeam(null)
        router.replace('/team')
      })
    }

    userService.updateRemoteUserInfo()

    return {
      t,
      ...toRefs(data),
      IS_PRIVATE,
      nameNode,
      teamInfo,
      exitCodeNode,
      uninstallCodeNode,
      editDisabled,
      monogram,
      smsVerifyVisible,
      confirmVerifyDeleteTeam,
      deleteTeam,
      async handleEditName () {
        data.nameEdit = true
        await nextTick()
        nameNode.value.focus()
      },
      async handleEditUninstallCode () {
        data.unInstallCodeEdit = true
        await nextTick()
        uninstallCodeNode.value.focus()
      },
      async handleEditExitCode () {
        data.exitCodeEdit = true
        await nextTick()
        exitCodeNode.value.focus()
      },
      saveName () {
        // 回车保存导致输入框卸载后可能再次触发 blur，此时直接忽略
        if (!data.nameEdit) return
        if (!(/^[\s\S]{1,30}$/.test(data.name))) {
          data.nameErrorFlag = true
          nameNode.value.focus()
          return
        }
        data.nameEdit = false
        data.nameErrorFlag = false
        centralControlService.updateName(data.name).then(() => {
          teamInfo.name = data.name
          team.updateCurrentTeam(teamInfo as TeamInfo)
        }).catch(() => {
          data.name = teamInfo.name
        })
      },
      saveExitCode () {
        if (!data.exitCodeEdit) return
        if (data.exitCode.length < PASS_MIN || !(/^[A-Za-z0-9]{6,25}$/.test(data.exitCode))) {
          data.exitCodeErrorFlag = true
          exitCodeNode.value.focus()
          return
        }
        data.exitCodeEdit = false
        data.exitCodeErrorFlag = false
        teamInfo.exit_code = data.exitCode
        team.updateCurrentTeam(teamInfo as TeamInfo)
        centralControlService.updateExitCode(teamInfo.exit_code)
      },
      saveUninstallCode () {
        if (!data.unInstallCodeEdit) return
        if (data.uninstallCode.length < PASS_MIN || !(/^[A-Za-z0-9]{6,25}$/.test(data.uninstallCode))) {
          data.uninstallCodeErrorFlag = true
          uninstallCodeNode.value.focus()
          return
        }
        data.unInstallCodeEdit = false
        data.uninstallCodeErrorFlag = false
        teamInfo.uninstall_code = data.uninstallCode
        team.updateCurrentTeam(teamInfo as TeamInfo)
        centralControlService.updateUninstallCode(teamInfo.uninstall_code)
      },
    }
  }
})
